import { useMutation } from '@tanstack/react-query';
import { ShipperDetails } from 'api/models/Deliveries';
import { useRef } from 'react';

export type PrintDataDetails = {
  deliveryId?: string;
  surgeryId?: string;
  returnDetails?: ShipperDetails;
  returnedByRepId?: string;
};

type ApiCallConfig = PrintDataDetails & {
  apiCall: (params: PrintDataDetails) => Promise<any>;
};

export const usePrintApi = ({ apiCall, deliveryId, surgeryId, returnDetails, returnedByRepId }: ApiCallConfig) => {
  const printDataDetails = useRef<PrintDataDetails>({ deliveryId, surgeryId, returnDetails, returnedByRepId });

  const query = useMutation({
    mutationFn: async () => {
      if (!printDataDetails.current.deliveryId && !printDataDetails.current.surgeryId) {
        throw new Error('deliveryId or surgeryId is required');
      }

      const response = await apiCall(printDataDetails.current);

      // Handling response as Blob
      let pdfBlob: Blob;
      if (response instanceof Blob) {
        pdfBlob = response;
      } else if (response && (response as any).data instanceof Blob) {
        pdfBlob = (response as any).data;
      } else {
        throw new Error('Unexpected response format');
      }

      if (!pdfBlob) {
        throw new Error('Response is not a Blob');
      }

      return pdfBlob;
    },
  });

  // Function to manually trigger the query and fetch the result
  const fetchPdf = async (details?: PrintDataDetails) => {
    if (details?.deliveryId || details?.surgeryId) {
      printDataDetails.current = details;
    }

    const result = await query.mutateAsync();
    if (!result) {
      throw new Error('No data available to open PDF');
    }
    return result;
  };

  return {
    fetchPdf,
    loading: query.isLoading,
    error: query.error,
  };
};
